import { ArrowRight, Loader2, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/shared/ui/Button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/shared/ui/Dialog";
import { Input } from "@/shared/ui/Input";
import { accountingService } from "../../services/accounting.service";
import { formatAmount } from "./utils";
import type { BankMovement } from "../../types/accounting.types";

type MoveToMovementDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  sourceBankMovementId: string
  sourceMovement: BankMovement | null
  type: "allocations" | "settlements"
  selectedCount: number
  selectedDebit: number
  selectedCredit: number
  onConfirm: (targetBankMovementId: string) => Promise<void>
  isMoving: boolean
};

const formatDate = (value: string | null) => {
  if (!value) return "—";
  const d = new Date(value);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const MoveToMovementDialog = ({
  open,
  onOpenChange,
  sourceBankMovementId,
  sourceMovement,
  type,
  selectedCount,
  selectedDebit,
  selectedCredit,
  onConfirm,
  isMoving,
}: MoveToMovementDialogProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<BankMovement[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState<BankMovement | null>(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setSearchResults([]);
      setSelectedTarget(null);
    }
  }, [open]);

  // Search for bank movements
  useEffect(() => {
    if (!open || searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const result = await accountingService.listBankMovements({
          label: searchQuery.trim(),
          limit: 10,
        });
        if (!controller.signal.aborted) {
          setSearchResults(
            result.bankMovements.filter((m) => m.id !== sourceBankMovementId && !m.locked),
          );
        }
      }
      catch {
        if (!controller.signal.aborted) setSearchResults([]);
      }
      finally {
        if (!controller.signal.aborted) setIsSearching(false);
      }
    }, 300);

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [searchQuery, open, sourceBankMovementId]);

  const handleConfirm = useCallback(async () => {
    if (!selectedTarget) return;
    await onConfirm(selectedTarget.id);
  }, [selectedTarget, onConfirm]);

  const itemLabel = type === "allocations" ? "écriture(s)" : "règlement(s)";

  // Balance = movement credit - debit + allocated debit - credit.
  const sourceCurrentBalance = sourceMovement?.allocationBalance ?? 0;
  const netAmount = selectedDebit - selectedCredit;
  const sourceProjectedBalance = sourceCurrentBalance - netAmount;

  if (selectedTarget) {
    return (
      <Dialog open={open} onOpenChange={(v) => { if (!isMoving) onOpenChange(v); }}>
        <DialogContent className="sm:max-w-lg" hasTitle>
          <DialogHeader>
            <DialogTitle className="text-base">Confirmer le déplacement</DialogTitle>
            <DialogDescription>
              {selectedCount}
              {" "}
              {itemLabel}
              {" "}
              {type === "allocations" ? "sélectionnée(s)" : "sélectionné(s)"}
              {" "}
              seront déplacé(es) vers le mouvement cible.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-[12px]">
            {/* Source */}
            <div className="rounded-md border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Source</div>
              <div className="font-medium text-slate-800 truncate">{sourceMovement?.label || sourceBankMovementId}</div>
              <div className="text-slate-500 mt-0.5">{formatDate(sourceMovement?.date ?? null)}</div>
              {type === "allocations" && (
                <div className="flex items-center gap-2 mt-1.5 text-[11px]">
                  <span className="text-slate-500">Solde affectation :</span>
                  <span className="tabular-nums">
                    {formatAmount(sourceCurrentBalance)}
                    {" "}
                    €
                  </span>
                  <ArrowRight className="h-3 w-3 text-slate-400" />
                  <span className="tabular-nums font-medium">
                    {formatAmount(sourceProjectedBalance)}
                    {" "}
                    €
                  </span>
                </div>
              )}
            </div>

            {/* Target */}
            <div className="rounded-md border border-indigo-200 bg-indigo-50/40 p-3">
              <div className="text-[10px] uppercase tracking-wide text-indigo-500 mb-1">Cible</div>
              <div className="font-medium text-indigo-900 truncate">{selectedTarget.label}</div>
              <div className="text-indigo-600 mt-0.5">
                {formatDate(selectedTarget.date)}
                {" "}
                —
                {selectedTarget.accountName || "—"}
              </div>
              {type === "allocations" && (
                <div className="flex items-center gap-2 mt-1.5 text-[11px]">
                  <span className="text-indigo-500">Solde affectation :</span>
                  <span className="tabular-nums">
                    {formatAmount(selectedTarget.allocationBalance ?? 0)}
                    {" "}
                    €
                  </span>
                  <ArrowRight className="h-3 w-3 text-indigo-400" />
                  <span className="tabular-nums font-medium">
                    {formatAmount((selectedTarget.allocationBalance ?? 0) + netAmount)}
                    {" "}
                    €
                  </span>
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
              {selectedCount}
              {" "}
              {itemLabel}
              {" "}
              — Débit :
              {" "}
              {formatAmount(selectedDebit)}
              {" "}
              € / Crédit :
              {" "}
              {formatAmount(selectedCredit)}
              {" "}
              €
              {type === "settlements" && (
                <div className="mt-1">Les écritures associées aux règlements seront également déplacées.</div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="text-[12px]"
              onClick={() => setSelectedTarget(null)}
              disabled={isMoving}
            >
              Retour
            </Button>
            <Button
              type="button"
              className="text-[12px]"
              onClick={handleConfirm}
              disabled={isMoving}
            >
              {isMoving
                ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    Déplacement...
                  </>
                )
                : "Confirmer le déplacement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" hasTitle>
        <DialogHeader>
          <DialogTitle className="text-base">Déplacer vers un autre mouvement</DialogTitle>
          <DialogDescription>
            Recherchez le mouvement cible par libellé pour y déplacer
            {" "}
            {selectedCount}
            {" "}
            {itemLabel}
            .
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher par libellé (min. 2 caractères)..."
              className="pl-9 h-9 text-[13px]"
              autoFocus
            />
            {isSearching && (
              <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
            )}
          </div>

          <div className="max-h-[300px] overflow-y-auto space-y-1.5">
            {!isSearching && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
              <div className="text-center py-6 text-[12px] text-slate-500">
                Aucun mouvement trouvé.
              </div>
            )}

            {searchResults.map((movement) => (
              <button
                key={movement.id}
                type="button"
                className="w-full text-left rounded-md border border-slate-200 bg-white px-3 py-2.5 hover:bg-slate-50 transition-colors"
                onClick={() => setSelectedTarget(movement)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-medium text-slate-800 truncate">
                      {movement.label}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {formatDate(movement.date)}
                      {" "}
                      —
                      {movement.accountName || "—"}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {(movement.debit ?? 0) > 0 && (
                      <div className="text-[11px] tabular-nums text-red-600">
                        {formatAmount(movement.debit ?? 0)}
                        {" "}
                        D
                      </div>
                    )}
                    {(movement.credit ?? 0) > 0 && (
                      <div className="text-[11px] tabular-nums text-emerald-600">
                        {formatAmount(movement.credit ?? 0)}
                        {" "}
                        C
                      </div>
                    )}
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      Solde :
                      {" "}
                      {formatAmount(movement.allocationBalance ?? 0)}
                      {" "}
                      €
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MoveToMovementDialog;
